// src/components/EventEditor.jsx
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import FormDesigner from './events/SimpleFormDesigner';

const STARTER_SCHEMA = {
  fields: [
    { type:'text',   name:'first_name', label:'Vorname / First name', required:true },
    { type:'text',   name:'last_name',  label:'Nachname / Last name', required:true },
    { type:'email',  name:'email',      label:'E-Mail',               required:true },
    { type:'radio',  name:'mode',       label:'Teilnahme', required:true,
      options:[ {value:'onsite',label:'Vor Ort / Onsite'}, {value:'online',label:'Online'} ] },
    { type:'consent',name:'consent_recording',
      text:'Ich habe zur Kenntnis genommen, dass die Veranstaltung aufgezeichnet wird. / I acknowledge the event will be recorded.',
      required:true },
      { type:'consent', name:'privacy_ack', required:true,
        html:'Ich habe die <a href="https://digitecstudieren.de/datenschutz.html" target="_blank" rel="noopener">Datenschutzerklärung</a> zur Kenntnis genommen.' }
  ]
};

export default function EventEditor({ id=0, onCancel, onSaved }){
  const [form,setForm]=useState({
    id:0, slug:'', title:'', description:'',
    start_at:'', end_at:'', location:'', hero_image_url:'',
    capacity:'', is_published:false,
    registration_start_at:'', registration_end_at:'',
    form_schema: STARTER_SCHEMA
  });
  const [err,setErr]=useState('');
  const [loading,setLoading]=useState(false);
  const [showJson,setShowJson]=useState(false);

  useEffect(()=>{
    if (!id) return;
    (async()=>{
      try{
        setLoading(true);
        const res = await apiFetch(`admin/event_detail.php?id=${id}`);
        const js  = await res.json();
        if(!js.ok) throw new Error(js.error||'Fehler');
        const ev = js.event;
        setForm(prev => ({
          ...prev,
          ...ev,
          capacity: ev.capacity ?? '',
          is_published: !!Number(ev.is_published),
          form_schema: ev.form_schema || STARTER_SCHEMA
        }));
      }catch(e){ setErr(e.message||'Laden fehlgeschlagen'); }
      finally{ setLoading(false); }
    })();
    // eslint-disable-next-line
  },[id]);

  const save = async ()=>{
    try{
      setErr(''); setLoading(true);
      const res = await apiFetch('admin/event_upsert.php', {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify(form)
      });
      const js = await res.json();
      if(!js.ok) throw new Error(js.error||'Speichern fehlgeschlagen');
      onSaved?.(js.id);
    }catch(e){ setErr(e.message||'Fehler'); }
    finally{ setLoading(false); }
  };

  return (
    <div>
      <button className="secondary" onClick={onCancel}>← Zurück</button>
      <h3 style={{marginTop:12}}>{id ? 'Event bearbeiten' : 'Neues Event'}</h3>

      {err && <div className="card" style={{color:'crimson',borderColor:'crimson'}}>{err}</div>}
      {loading && <div className="card">Lade…</div>}

      <div className="grid" style={{display:'grid',gridTemplateColumns:'1fr',gap:'1rem'}}>
        {/* Basisdaten */}
        <div className="card">
          <div className="grid" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
            <div>
              <label>Slug</label>
              <input value={form.slug} onChange={e=>setForm({...form,slug:e.target.value})}/>
              <label>Titel</label>
              <input value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/>
              <label>Beschreibung</label>
              <textarea rows={6} value={form.description||''} onChange={e=>setForm({...form,description:e.target.value})}/>
              <label>Ort</label>
              <input value={form.location||''} onChange={e=>setForm({...form,location:e.target.value})}/>
              <label>Header-Bild URL</label>
              <input value={form.hero_image_url||''} onChange={e=>setForm({...form,hero_image_url:e.target.value})}/>
              <label>Kapazität (optional)</label>
              <input type="number" value={form.capacity??''} onChange={e=>setForm({...form,capacity:e.target.value})}/>
              <label><input type="checkbox" checked={!!form.is_published} onChange={e=>setForm({...form,is_published:e.target.checked})}/> Veröffentlicht</label>
            </div>
            <div>
              <label>Start (Event)</label>
              <input type="datetime-local" value={form.start_at||''} onChange={e=>setForm({...form,start_at:e.target.value})}/>
              <label>Ende (Event)</label>
              <input type="datetime-local" value={form.end_at||''} onChange={e=>setForm({...form,end_at:e.target.value})}/>
              <label>Registrierung ab</label>
              <input type="datetime-local" value={form.registration_start_at||''} onChange={e=>setForm({...form,registration_start_at:e.target.value})}/>
              <label>Registrierung bis</label>
              <input type="datetime-local" value={form.registration_end_at||''} onChange={e=>setForm({...form,registration_end_at:e.target.value})}/>
            </div>
          </div>
        </div>

        {/* Form-Designer */}
        <div className="card">
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <h4 style={{margin:0}}>Anmeldeformular</h4>
            <label style={{fontSize:14}}>
              <input type="checkbox" checked={showJson} onChange={e=>setShowJson(e.target.checked)} /> JSON anzeigen
            </label>
          </div>
          <div style={{marginTop:12}}>
            <FormDesigner
              value={form.form_schema}
              onChange={(schema)=>setForm({...form, form_schema: schema})}
            />
          </div>

          {showJson && (
            <details style={{marginTop:12}} open>
              <summary>Form-Schema (nur für Fortgeschrittene bearbeiten)</summary>
              <textarea rows={14}
                value={JSON.stringify(form.form_schema,null,2)}
                onChange={e=>{
                  try{ const j=JSON.parse(e.target.value); setForm({...form,form_schema:j}); setErr(''); }
                  catch{ setErr('Form-Schema ist kein valides JSON'); }
                }}/>
            </details>
          )}
        </div>
      </div>

      <div style={{marginTop:12, display:'flex', gap:8}}>
        <button className="primary" onClick={save}>Speichern</button>
        <button className="secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </div>
  );
}